import {
  Ref, computed, ref, watch,
} from '@vue/composition-api';

/**
 * provides features for formatting value of text input
 *
 * @param {Object} props component properties
 * @returns {Object} `formatValue` as Function and `formattedValue` as Computed
 */
export const useNumberFormat = (
  props: {
    value: number;
    locale: string;
    minDecimal: number;
    maxDecimal: number;
    useGrouping: boolean;
  },
) => {
  const formatValue = (modelValue: number) => {
    if (modelValue != null) {
      const formatter = new Intl.NumberFormat(props.locale,
        {
          minimumFractionDigits: props.minDecimal,
          maximumFractionDigits: props.maxDecimal,
          useGrouping: props.useGrouping,
        });

      return formatter.format(modelValue);
    }

    return '';
  };

  const formattedValue = computed(() => formatValue(props.value));
  return { formatValue, formattedValue };
};

/**
 * provides features for parsing number value from text input
 *
 * @param {object} props
 * @returns {object} `groupingChar` as `decimalChar` Computed and `parseValue` as Function
 */
export const useNumberParsing = (props: { locale: string }) => {
  const groupingChar = computed(() => {
    const formatter = Intl.NumberFormat(props.locale, { useGrouping: true });
    return formatter.format(10000).replace(/\d+/g, '').charAt(0);
  });

  const decimalChar = computed(() => {
    const formatter = Intl.NumberFormat(props.locale, {});
    return formatter.format(1.1).replace(/\d+/g, '');
  });

  const parseValue = (value: string) => {
    const cleanedText = value
      .trim()
      .replace(new RegExp(`\\${groupingChar.value}`, 'g'), '')
      .replace(new RegExp(`\\${decimalChar.value}`, 'g'), '.');

    if (cleanedText.length > 0) {
      const parsedValue = parseFloat(cleanedText);
      if (Number.isNaN(parsedValue)) return null;

      return parsedValue;
    }
    return null;
  };

  return { groupingChar, decimalChar, parseValue };
};

/**
 * provides features for input validation
 *
 * @param internalValue component value
 * @param props component properties
 * @returns {object}
 *   * `valid` - boolean ref
 *   * `errorMessage` - string ref
 *   * `validate` - actual validation function
 */
export const useNumberValidation = (
  internalValue: Ref<number>,
  props: {
    min: number;
    max: number;
    validator: Function;
    validState: CustomInputValidationState;
  },
) => {
  const valid = ref(true);
  const errorMessage = ref('');

  const validate = () => {
    if (props.min != null && internalValue.value < props.min) {
      valid.value = false;
      errorMessage.value = `value is below ${props.min}`;
    } else if (props.max != null && internalValue.value > props.max) {
      valid.value = false;
      errorMessage.value = `value is above ${props.max}`;
    } else if (props.validState != null && !props.validState.customValid) {
      valid.value = props.validState.customValid;
      errorMessage.value = props.validState.customErrorMessage;
    } else if (props.validator != null) {
      const { customValid, customErrorMessage } = props.validator(internalValue.value);
      valid.value = customValid;
      errorMessage.value = customErrorMessage;
    }
  };

  watch(internalValue, validate);

  watch(() => props.validState, validate, { deep: true });

  return { valid, errorMessage, validate };
};

/**
 * describes the specifications for input events
 *
 * @export
 * @param internalValue - the value of the input
 * @param formatValue - a function to format the value to the set locale
 * @param decimalChar - current decimal char depending on locale
 * @param props - component properties
 * @param emit - component emit function
 * @param validate - `validate` function from {@link useNumberValidation}
 * @param parseValue - `parseValue` function from {@link useNumberParsing}
 * @returns
 *   * `onInputBlur` - function used by `@blur` event of text input
 *   * `onInputKeyPress` - function used by `@keydown` event of text input
 */
export const useNumberInputEvents = (
  internalValue: Ref<number>,
  formatValue: (val: number) => string,
  decimalChar: Ref<string>,
  props: { min: number; max: number; maxDecimal: number; step: number, preventEnter: boolean },
  emit: Function,
  parseValue: Function,
) => {
  const isInputFocused = ref<boolean>(false);

  const updateValue = ($event: Event) => {
    const inputElement = $event.target as HTMLInputElement;
    inputElement.value = formatValue(internalValue.value);
    emit('change', internalValue.value);
  };

  const allowMinusSign = computed(() => props.min == null || props.min != null && props.min < 0);
  const allowDecimal = computed(() => props.maxDecimal > 0);

  const onInputBlur = ($event: FocusEvent) => {
    $event.preventDefault();
    isInputFocused.value = false;
    const inputElement = $event.target as HTMLInputElement;
    const parsedValue = parseValue(inputElement.value);

    internalValue.value = parsedValue;
    updateValue($event);
    emit('blur');
  };

  const onInputFocus = () => {
    isInputFocused.value = true;
    emit('focus');
  };

  const onInputKeyPress = ($event: KeyboardEvent) => {
    if ($event.ctrlKey) return;
    if (props.preventEnter && $event.key === 'Enter') {
      $event.preventDefault();
    }

    if ($event.key === 'ArrowUp') {
      internalValue.value += props.step;
      updateValue($event);
      return;
    }

    if ($event.key === 'ArrowDown') {
      internalValue.value -= props.step;
      updateValue($event);
      return;
    }

    // filter out invalid character inputs
    const minusChar = allowMinusSign.value ? '-' : '';
    const regexpString = `[0-9\\${minusChar}\\${decimalChar.value}]`;
    if ($event.key.length === 1 && !new RegExp(regexpString).test($event.key)) {
      $event.preventDefault();
      return;
    }

    const inputElement = $event.target as HTMLInputElement;
    const inputElementValue = inputElement.value;
    const selectionStart = inputElement.selectionStart != null ? inputElement.selectionStart : -1;
    const selectionEnd = inputElement.selectionEnd != null ? inputElement.selectionEnd : -1;
    const selectedText = inputElementValue.slice(selectionStart, selectionEnd);

    // filter out decimal sign
    if ($event.key === decimalChar.value && (
      // decimal is not allowed (minDecimal === 0)
      !allowDecimal.value
      // decimal is allowed
      || (
        allowDecimal.value
        // is already in text
        && inputElementValue.indexOf(decimalChar.value) > 0
      )
      // marked text does not containing the decimal char
      && selectedText.indexOf(decimalChar.value) < 0
    )) {
      $event.preventDefault();
      return;
    }

    // filter out minus sign
    if ($event.key === '-' && (
      // minus sign is not allowed (min <= 0)
      !allowMinusSign.value
      // minus sign is allowed but is already in text
      || (allowMinusSign.value && inputElementValue.indexOf('-') >= 0 && selectedText.indexOf(minusChar) < 0)
      // cursor is at the wrong position (not 0)
      || selectionStart > 0
    )) {
      $event.preventDefault();
      return;
    }

    // prevent more than max allowed decimal
    const fractionText = inputElementValue.match(
      new RegExp(`\\${decimalChar.value}(\\d+)$`),
    );
    const decimalIndex = inputElementValue.indexOf(decimalChar.value);
    if ($event.key.length === 1 && $event.key.match(/[0-9]/)
      // input of decimal is allowed
      && allowDecimal.value
      // cursor is after decimal separator
      && selectionStart === selectionEnd && selectionStart > decimalIndex
      // length exceeds maxDecimal
      && fractionText != null
      && fractionText.length > 1
      && fractionText[1].length >= props.maxDecimal
    ) {
      $event.preventDefault();
    }
  };

  return {
    isInputFocused, onInputBlur, onInputFocus, onInputKeyPress,
  };
};

export interface CustomInputValidationState {
  customValid: boolean;
  customErrorMessage: string;
}
